import React, { useState, useEffect, useRef } from 'react';
import { Box, InputBase, Stack, IconButton, Tooltip, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { fixGrammarAndSpell } from '../../../services/ai.service';

const TitleSection = ({ initialTitle, onSave, selectedData, sx }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(initialTitle);
  const [originalTitle, setOriginalTitle] = useState(initialTitle);
  const [isFixing, setIsFixing] = useState(false);
  const [fixed, setFixed] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEditing = () => {
    setOriginalTitle(title);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    const result = await onSave({ title });
    if (result?.ok) setIsEditing(false);
  };

  const handleCancel = () => {
    setTitle(originalTitle);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') handleCancel();
  };

  const handleFix = async () => {
    if (!title.trim() || isFixing) return;
    setIsFixing(true);
    try {
      const res = await fixGrammarAndSpell(title.trim());
      const corrected = res?.correctedText;
      if (corrected) {
        setTitle(corrected);
        setFixed(true);
        setTimeout(() => setFixed(false), 2000);
      }
    } catch (_) {
      // silently ignore — user can retry
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isEditing ? (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <InputBase
            inputRef={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            sx={{
              width: '100%',
              fontSize: '15px',
              fontWeight: 600,
              color: '#0F172A',
              lineHeight: 1.4,
              pl: 1.2,
              pr: 1,
              py: 0.6,
              borderRadius: '7px',
              border: '2px solid #0D9488',
              background: '#fff',
              boxShadow: '0 0 0 3px rgba(13,148,136,0.12)',
              '& input': { paddingRight: '64px' },
            }}
          />
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <Tooltip title="Fix spelling & grammar (AI)">
              <IconButton
                size="small"
                onClick={handleFix}
                disabled={isFixing || !title.trim()}
                sx={{
                  bgcolor: fixed ? '#F0FDF4' : '#FAF5FF',
                  color: fixed ? '#16A34A' : '#7C3AED',
                  borderRadius: '6px',
                  width: 26, height: 26,
                  border: `1px solid ${fixed ? '#86EFAC' : '#DDD6FE'}`,
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: '#EDE9FE', color: '#6D28D9' },
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                {isFixing
                  ? <CircularProgress size={11} sx={{ color: '#7C3AED' }} />
                  : <AutoFixHighIcon sx={{ fontSize: 13 }} />
                }
              </IconButton>
            </Tooltip>
            <Tooltip title="Save (Enter)">
              <IconButton
                size="small"
                onClick={handleSave}
                sx={{
                  bgcolor: '#0D9488', color: '#fff', borderRadius: '6px',
                  width: 26, height: 26,
                  '&:hover': { bgcolor: '#0B7A70' },
                }}
              >
                <CheckIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel (Esc)">
              <IconButton
                size="small"
                onClick={handleCancel}
                sx={{
                  bgcolor: '#E2E8F0', color: '#475569', borderRadius: '6px',
                  width: 26, height: 26,
                  '&:hover': { bgcolor: '#FEE2E2', color: '#EF4444' },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ) : (
        <Box
          onClick={handleStartEditing}
          sx={{
            position: 'relative',
            cursor: 'text',
            borderRadius: '8px',
            px: 1.4,
            py: 0.8,
            border: '2px solid transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              border: '2px solid #CBD5E1',
              background: '#F1F5F9',
              '& .edit-hint': { opacity: 1 },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: title ? '#0F172A' : '#94A3B8',
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1.4,
              display: 'block',
              wordBreak: 'break-word',
              ...sx,
            }}
          >
            {title || 'Untitled task'}
          </Box>
          <Box
            className="edit-hint"
            sx={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0, transition: 'opacity 0.15s',
              color: '#64748B',
              display: 'flex', alignItems: 'center',
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TitleSection;
